import { inject, Injectable } from '@angular/core';
import { QueryCacheResults, QueryKey, WithQueyKey } from './query-cache.interfaces';
import { delay, filter, Observable, of, startWith, Subject, switchMap, tap } from 'rxjs';
import { HttpClient, HttpEventType, HttpRequest, HttpResponse } from '@angular/common/http';
import { CACHE_KEY_SYMBOL, DEFAULT_STALE_TIME } from './cache.tokens';
import { ENV } from '../env.token';

@Injectable({
  providedIn: 'root',
})
export class QueryCacheService {
  private static readonly DEFAULT_STALE_TIME = DEFAULT_STALE_TIME;
  private readonly queryCache = new Map<QueryKey, QueryCacheResults>();
  private readonly httpClient = inject(HttpClient);
  private readonly env = inject(ENV);

  private readonly invalidationSubject = new Subject<QueryKey>();

  /**
   * Perform an HTTP GET request that will automatically refresh when cache becomes stale or invalidated
   *
   * To use, just provide the same arguments you would provide to HttpClient.get()
   */
  public get<T>(...args: Parameters<HttpClient['get']>) {
    const [url, options] = args;

    const req = new HttpRequest('GET', url, options);
    const key = this.createKeyFromRequest(req);

    const observable = this.httpClient.get<T>(...args);

    return this.toLiveObservable(observable, key);
  }

  /**
   * Wraps an observable with the invalidation check
   * so that whenever the provided key expires, the observable will re-run
   */
  private toLiveObservable<T>(observable: Observable<T>, key: QueryKey) {
    return this.invalidationSubject.asObservable().pipe(
      startWith(key),
      filter((invalidatedKey) => this.keyIs(invalidatedKey, key)),
      delay(20), // Allow some time for the cache invalidation stack to complete
      switchMap(() => observable)
    );
  }

  /**
   * Attempts to convert a cold observable into a live one
   * Does so by extracting the stored query key
   * If the response body of the observable is null, then this has no effect
   *
   * Due to technical limitations, technically this will subscribe twice to the observable
   * so don't use this with calls that shouldn't be repeated (such as mutations)
   */
  public asLive<T>(observable: Observable<T>): Observable<T> {
    return observable.pipe(
      switchMap((value) => {
        // This is the response we got back. Assuming it's an object, check if it contains the query key, and store it
        const asPossibleKeyed = value as WithQueyKey<T>;
        if (asPossibleKeyed && typeof asPossibleKeyed === 'object' && asPossibleKeyed[CACHE_KEY_SYMBOL]) {
          return this.toLiveObservable(observable, asPossibleKeyed[CACHE_KEY_SYMBOL]);
        }

        // No key was found, return the value as-is
        return of(value);
      })
    );
  }

  /**
   * Perform an HTTP POST request that will invalidate the specified cache key on success.
   *
   * Use the `invalidate` option to explicitly bust cache entries that this mutation affects.
   */
  public post<T>(
    url: string,
    body: Parameters<HttpClient['post']>[1],
    options?: Parameters<HttpClient['post']>[2] & { invalidate?: QueryKey | string | readonly (QueryKey | string)[] }
  ): Observable<T> {
    const { invalidate } = options ?? {};
    return this.httpClient.post<T>(url, body).pipe(
      tap(() => {
        if (invalidate !== undefined) {
          for (const key of this.toKeyList(invalidate)) {
            this.invalidate(key);
          }
        }
      })
    );
  }

  /**
   * Normalizes the `invalidate` option into a list, so a mutation can bust several unrelated caches
   * (ie. a contact mutation affects both the contact list and the owner grid that counts its contacts)
   */
  private toKeyList(invalidate: QueryKey | string | readonly (QueryKey | string)[]): readonly (QueryKey | string)[] {
    if (typeof invalidate === 'string') {
      return [invalidate];
    }

    // A QueryKey is a [url, params] tuple; anything else is already a list of keys
    const isQueryKey =
      invalidate.length === 2 &&
      typeof invalidate[0] === 'string' &&
      typeof invalidate[1] === 'object' &&
      invalidate[1] !== null &&
      !Array.isArray(invalidate[1]);

    return isQueryKey ? [invalidate as QueryKey] : (invalidate as readonly (QueryKey | string)[]);
  }

  public createKeyFromRequest(req: HttpRequest<unknown>): QueryKey {
    const url = req.url;
    const params = req.params.keys().reduce(
      (acc, key) => {
        acc[key] = req.params.getAll(key);
        return acc;
      },
      {} as Record<string, string[] | null>
    );

    return [url, params];
  }

  private getCacheFor(keyOrRequest: QueryKey | HttpRequest<unknown>) {
    const key = keyOrRequest instanceof HttpRequest ? this.createKeyFromRequest(keyOrRequest) : keyOrRequest;
    const time = Date.now();

    for (const [cacheKey, cached] of this.queryCache.entries()) {
      if (this.keyIs(cacheKey, key)) {
        if (time > cached.expires) {
          this.queryCache.delete(cacheKey);
          return undefined;
        }

        return cached;
      }
    }

    return undefined;
  }

  public getCachedResponse<T>(keyOrRequest: QueryKey | HttpRequest<T>): HttpResponse<T> | undefined {
    return this.getCacheFor(keyOrRequest)?.data as HttpResponse<T> | undefined;
  }

  public getCachedData<T>(keyOrRequest: QueryKey | HttpRequest<T>): T | undefined {
    return this.getCacheFor(keyOrRequest)?.data.body as T | undefined;
  }

  public setCachedResponse(
    keyOrRequest: QueryKey | HttpRequest<unknown>,
    data: HttpResponse<unknown>,
    staleTime = QueryCacheService.DEFAULT_STALE_TIME
  ): void {
    const key = keyOrRequest instanceof HttpRequest ? this.createKeyFromRequest(keyOrRequest) : keyOrRequest;

    // Make sure to invalidate any existing cache entry
    this.invalidate(key, true);
    this.queryCache.set(key, { data, expires: Date.now() + staleTime });

    if (data.type === HttpEventType.Response && data.ok && data.body && typeof data.body === 'object') {
      const body = data.body as WithQueyKey<unknown>;
      Object.defineProperty(body, CACHE_KEY_SYMBOL, { configurable: true, value: key, writable: true, enumerable: false });
    }
  }

  public setCachedData(
    keyOrRequest: QueryKey | HttpRequest<unknown>,
    data: unknown,
    staleTime = QueryCacheService.DEFAULT_STALE_TIME
  ): void {
    // Wrap the data in an HttpResponse, so that it fits the expected type
    const response = new HttpResponse({
      body: data,
      status: 200,
    });
    this.setCachedResponse(keyOrRequest, response, staleTime);
  }

  /**
   * Invalidates all queries that contain at least specified key
   *
   * This means that all queries that start with the URL part, and contain all of the key/value pairs of the params
   * will be invalidated, ie. both removed from cache and flagged for reloading
   *
   * @param partialKeyOrUrl Partial QueryKey or URL string to invalidate
   * @param isExact If true, only invalidate exact matches (default: false)
   *
   * @returns true if any queries were invalidated, false otherwise
   */
  public invalidate(partialKeyOrUrl: QueryKey | string, isExact = false): boolean {
    const partialKey = typeof partialKeyOrUrl === 'string' ? ([partialKeyOrUrl, {}] as QueryKey) : partialKeyOrUrl;
    let invalidated = false;

    for (const key of this.queryCache.keys()) {
      const isMatch = isExact ? this.keyIs(key, partialKey) : this.keyContains(key, partialKey);
      if (isMatch) {
        this.queryCache.delete(key);
        this.invalidationSubject.next(key);
        invalidated = true;
      }
    }

    return invalidated;
  }

  /**
   * Normalizes a URL by stripping the apiBaseUrl prefix if present.
   * This ensures cache keys work consistently between local (relative URLs)
   * and deployed environments (absolute URLs with apiBaseUrl prefix).
   */
  private normalizeUrl(url: string): string {
    const apiBaseUrl = this.env.apiBaseUrl;
    if (!apiBaseUrl) {
      return url;
    }

    // Remove trailing slash from apiBaseUrl for consistent comparison
    const normalizedApiUrl = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;

    // If the URL starts with the apiBaseUrl, strip it to get the relative path
    if (url.startsWith(normalizedApiUrl)) {
      return url.substring(normalizedApiUrl.length);
    }

    return url;
  }

  /**
   * Checks if the 2 keys are "identical", in that all parts match
   * Used for cache retrieval logic
   */
  private keyIs(queryKeyA: QueryKey, queryKeyB: QueryKey): boolean {
    const [urlA, paramsA] = queryKeyA;
    const [urlB, paramsB] = queryKeyB;

    // Normalize URLs to handle both absolute (CDE) and relative (local) URLs
    const normalizedUrlA = this.normalizeUrl(urlA);
    const normalizedUrlB = this.normalizeUrl(urlB);

    if (normalizedUrlA !== normalizedUrlB) {
      return false;
    }

    const allKeys = new Set([...Object.keys(paramsA), ...Object.keys(paramsB)]);

    for (const key of allKeys) {
      if (JSON.stringify(paramsA[key]) !== JSON.stringify(paramsB[key])) {
        return false;
      }
    }

    return true;
  }

  /**
   * Checks if the fullKey contains the partialKey
   * Used for invalidation logic, where we want to invalidate multiple matching keys
   */
  private keyContains(fullKey: QueryKey, partialKey: QueryKey): boolean {
    const [fullUrl, fullParams] = fullKey;
    const [partialUrl, partialParams] = partialKey;

    // Normalize both URLs to handle absolute (CDE) vs relative (local) URL comparison
    const normalizedFullUrl = this.normalizeUrl(fullUrl);
    const normalizedPartialUrl = this.normalizeUrl(partialUrl);

    if (!normalizedFullUrl.startsWith(normalizedPartialUrl)) {
      return false;
    }

    for (const paramKey of Object.keys(partialParams)) {
      const fullParamsList = fullParams[paramKey];
      const partialParamsList = partialParams[paramKey];
      // If both are null, continue
      if (fullParams[paramKey] === null && partialParams[paramKey] === null) {
        continue;
      }

      // If one isn't an array, no match
      if (!Array.isArray(fullParamsList) || !Array.isArray(partialParamsList)) {
        return false;
      }

      for (let i = 0; i < partialParamsList.length; i++) {
        if (fullParamsList[i] !== partialParamsList[i]) {
          return false;
        }
      }
    }

    return true;
  }
}
